// useHiddenNests — hide-by-default nests on the Documents page. CTSK-522.
//
// A fixed seed list of nest slugs is filtered out of the Documents list and
// search results unless the operator reveals them via a chip. Revealed slugs
// persist per browser under `tasko.documents.hidden-nests.v1` (string[]).
//
// Hardcoded for now (see ticket): promote to a setting only if operators ask.
//
// Two deliberate rules:
//  - A nest the operator is explicitly scoped to (?nest=<slug> / nest chip)
//    is never hidden — otherwise a sidebar link to a hidden nest shows nothing.
//  - Counts come from a separate `/documents?tag=<nest tag>&limit=1` query
//    (page.total), not from the loaded page, so the chip is accurate even
//    though rows are filtered client-side.

import { useCallback, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { Tag } from '@captain-tasko/shared';
import { apiList } from '../lib/api.js';

export const HIDDEN_NESTS_DEFAULT: readonly string[] = ['external-clients'];
export const HIDDEN_NESTS_STORE_KEY = 'tasko.documents.hidden-nests.v1';

function readRevealed(): string[] {
  try {
    const raw = window.localStorage.getItem(HIDDEN_NESTS_STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export interface HiddenNestChip {
  slug: string;
  /** Total documents in the nest (not just the loaded page); null while loading. */
  count: number | null;
  revealed: boolean;
}

export function useHiddenNests(opts: { activeNestSlug: string | null; tags: Tag[] | undefined }) {
  const { activeNestSlug, tags } = opts;
  const [revealed, setRevealed] = useState<string[]>(() => readRevealed());

  const toggle = useCallback((slug: string) => {
    setRevealed((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      try {
        window.localStorage.setItem(HIDDEN_NESTS_STORE_KEY, JSON.stringify(next));
      } catch {
        /* private mode / quota — keep in-memory state only */
      }
      return next;
    });
  }, []);

  const tagIdBySlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const slug of HIDDEN_NESTS_DEFAULT) {
      const t = (tags ?? []).find((x) => x.name === `nest/${slug}`);
      if (t) m.set(slug, t.id);
    }
    return m;
  }, [tags]);

  const countQs = useQueries({
    queries: HIDDEN_NESTS_DEFAULT.map((slug) => {
      const tagId = tagIdBySlug.get(slug);
      return {
        queryKey: ['document-hidden-nest-count', slug, tagId ?? null],
        enabled: Boolean(tagId),
        staleTime: 15_000,
        queryFn: () => apiList<unknown>('/documents', { query: { tag: [tagId!], limit: 1, offset: 0 } }),
      };
    }),
  });

  // Slugs currently filtered out of the rows.
  const hiddenSet = useMemo(() => {
    const s = new Set<string>();
    for (const slug of HIDDEN_NESTS_DEFAULT) {
      if (!revealed.includes(slug) && slug !== activeNestSlug) s.add(slug);
    }
    return s;
  }, [revealed, activeNestSlug]);

  // Chips to render: only for nests that exist in this org and either still
  // hold documents or are currently revealed (so they can be re-hidden).
  const chips: HiddenNestChip[] = [];
  HIDDEN_NESTS_DEFAULT.forEach((slug, i) => {
    if (!tagIdBySlug.has(slug)) return;
    const q = countQs[i];
    const count = q?.data ? (q.data.page?.total ?? q.data.data.length) : null;
    const isRevealed = revealed.includes(slug);
    if (count === 0 && !isRevealed) return;
    chips.push({ slug, count, revealed: isRevealed });
  });

  return { hiddenSet, chips, toggle };
}
